import {useAnalytics} from '@github-ui/use-analytics'
import {AgentIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, type ButtonProps} from '@primer/react'
import type {SyntheticEvent} from 'react'

import type {ConflictMergeConditionPayload} from '../../../page-data/payloads/merge-box'
import {useAgentConflictResolutionComment} from '../../../hooks/mutations/use-agent-conflict-resolution-comment'

/**
 *
 * Renders the "Resolve conflicts" menu offering to either resolve conflicts in the web editor or start a Copilot agent session.
 */
export function ResolveConflictsAgentMenu({
  buttonId,
  conflictsWebEditorPath,
  onError,
  onActionSelect,
  onFixWithCopilot,
  webEditorConflictResolution,
  size,
}: {
  buttonId?: string
  conflictsWebEditorPath: string
  onError?: (message: string) => void
  onActionSelect?: (actionName: 'edit_conflicts_on_web' | 'fix_conflicts_with_copilot') => void
  onFixWithCopilot?: () => void
  webEditorConflictResolution: NonNullable<ConflictMergeConditionPayload['webEditorConflictResolution']>
  size?: ButtonProps['size']
}) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {startAgentConflictResolutionComment, isAgentConflictResolutionCommentPending} =
    useAgentConflictResolutionComment({
      onSuccess: () => {
        sendAnalyticsEvent('conflicts_section.start_agent_session', 'MERGEBOX_CONFLICTS_SECTION_START_AGENT_SESSION')
      },
      onError: () => {
        onError?.('Failed to start an agent session. Please try again.')
      },
    })

  const webEditorItemInactiveText = !webEditorConflictResolution.viewerCanResolve
    ? webEditorConflictResolution.viewerCannotResolve?.message
    : undefined
  const handleActionSelect = (
    event: SyntheticEvent,
    actionName: 'edit_conflicts_on_web' | 'fix_conflicts_with_copilot',
  ) => {
    if (!onActionSelect) return
    event.stopPropagation()
    onActionSelect(actionName)
  }
  const handleFixWithCopilot = () => {
    if (onFixWithCopilot) {
      onFixWithCopilot()
      sendAnalyticsEvent('conflicts_section.start_agent_session', 'MERGEBOX_CONFLICTS_SECTION_START_AGENT_SESSION')
      return
    }

    startAgentConflictResolutionComment()
  }

  return (
    <ActionMenu>
      <ActionMenu.Button
        id={buttonId}
        loading={!onFixWithCopilot && isAgentConflictResolutionCommentPending}
        size={size}
      >
        Resolve conflicts
      </ActionMenu.Button>
      <ActionMenu.Overlay align="end">
        <ActionList>
          <ActionList.LinkItem
            href={conflictsWebEditorPath}
            inactiveText={webEditorItemInactiveText}
            onClick={event => handleActionSelect(event, 'edit_conflicts_on_web')}
          >
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            Edit on the web
          </ActionList.LinkItem>
          <ActionList.Item
            onSelect={event => {
              handleActionSelect(event, 'fix_conflicts_with_copilot')
              handleFixWithCopilot()
            }}
          >
            <ActionList.LeadingVisual>
              <AgentIcon />
            </ActionList.LeadingVisual>
            Fix with Copilot
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
